import { Fields, YesObject } from '@yes.technology/react-toolkit'
import { array } from 'zod/v4'

const REGEX_PATTERN = /\{\{([^\}]+)\}\}/g

export interface ConcatFields {
  [fieldname: string]: string
}

export function concatenateField(
  object: YesObject,
  sourcemap: string,
  isIgnoring?: boolean
): string {
  if (isIgnoring) return ignoringConcatenateField(object, sourcemap)

  return sourcemap.replace(REGEX_PATTERN, (match, p1) => {
    const keys = p1.split('.')
    const value = getValueFromObject(object, keys)
    if (value !== undefined) {
      return value
    }
    return ''
  })
}

export function ignoringConcatenateField(object: YesObject, sourcemap: string) {
  const parts = sourcemap.split(/(\{\{[^\}]+\}\})/g)

  const result: Array<[string, boolean]> = []

  parts
    .map((part) => {
      const match = part.match(/\{\{(.*?)\}\}/)
      if (!match) return [part, true]

      const keys = match[1].split('.')
      const value = getValueFromObject(object, keys)
      if (value !== undefined) {
        return [value, false]
      }
      return ['', false]
    })
    .slice(1, -1)
    .forEach((pair, index, array) => {
      if ((pair[1] && index === 0) || (pair[1] === false && pair[0])) {
        result.push(pair as [string, boolean])
      }
      if (index + 1 === array.length && pair[1])
        result.push(pair as [string, boolean])
      if (pair[1] && (!array[index + 1][0] || result.length === 0)) return
      if (pair[1] && !result.at(-1)?.[1]) {
        result.push(pair as [string, boolean])
      }
    })

  return result
    .map((pair) => pair[0])
    .join('')
    .trim()
}

function getValueFromObject(
  obj: YesObject,
  keys: string[]
): string | undefined {
  let value: any = obj
  for (const key of keys) {
    if (value?.[key] !== undefined) {
      value = value[key]
    } else {
      return undefined
    }
  }
  return typeof value === 'string' ? value : undefined
}

export function concatenateFields(
  concatFields: ConcatFields,
  yesObject: YesObject
) {
  return Object.keys(concatFields || {}).reduce((acc, fieldname) => {
    if (concatFields[fieldname]) {
      return {
        ...acc,
        [fieldname]: concatenateField(yesObject, concatFields[fieldname])
      }
    }
    return acc
  }, yesObject)
}

export const concatenateDisplayText = ({
  concatFields,
  fields,
  columnName
}: {
  concatFields?: ConcatFields
  fields?: Fields
  columnName: string
}) => {
  const concatColumnPattern = concatFields?.[columnName]

  return (
    concatColumnPattern?.replace(REGEX_PATTERN, (substring, keys) => {
      const key = keys.split('.')[0]
      return fields?.[key]?.display_text.des || ''
    }) || fields?.[columnName]?.display_text.des
  )
}

export const extractFieldsFromConcatFields = (
  concatFields?: Record<string, string>,
  currentFields?: string[]
): string[] => {
  if (!concatFields || Object.keys(concatFields).length === 0) return []

  const result = new Set<string>()

  for (const pattern of Object.values(concatFields)) {
    const matches = pattern.match(REGEX_PATTERN)
    if (!matches) continue

    for (const match of matches) {
      const key = match.slice(2, -2)
      if (result.has(key)) continue

      const splitted = key.split('.').map((k) => k.trim())
      const mainKey = splitted[0]
      const attribute = splitted[splitted.length - 1]

      let formattedKey = mainKey

      if (attribute !== mainKey) {
        formattedKey += `.${attribute}`
      }

      if (!(currentFields ?? [])?.includes(mainKey)) {
        result.add(formattedKey)
      }
    }
  }

  return Array.from(result)
}

export const extractFieldsFromString = (concatenationString: string) => {
  const matches = concatenationString.match(REGEX_PATTERN)
  if (!matches) return []

  return matches.map((match) => {
    const key = match.slice(2, -2)

    const keyWithSubs = key.split('.')

    if (keyWithSubs.length === 1) return key

    return keyWithSubs.filter((subKey, index) => index % 2 === 0).join('.')
  })
}
